import { useState } from 'react'
import { KpiCard } from '../components/KpiCard'
import { AlertBadge } from '../components/AlertBadge'
import { useAdminData } from '../hooks/useAdminData'

interface BillingEvent {
  id: string
  stripe_event_id: string
  event_type: string
  customer_id?: string
  amount_usd?: number
  occurred_at: string
  created_at: string
}

interface BillingData {
  events: BillingEvent[]
  total_events: number
  mrr_waterfall: {
    new_mrr: number
    expansion_mrr: number
    churned_mrr: number
    net_mrr: number
  }
  failed_payments: BillingEvent[]
}

const EVENT_LABELS: Record<string, string> = {
  'customer.subscription.created': 'Nova subscription',
  'customer.subscription.updated': 'Subscription alterada',
  'customer.subscription.deleted': 'Churn',
  'invoice.paid': 'Pagamento',
  'invoice.payment_failed': 'Pagamento falhado',
}

export function BillingPage() {
  const [days, setDays] = useState(30)
  const { data, loading } = useAdminData<BillingData>(`/billing?days=${days}`, [days])

  if (loading || !data) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: 'var(--ink-muted)', fontFamily: 'var(--sans)' }}>
        {loading ? 'A carregar...' : 'Sem dados'}
      </div>
    )
  }

  const wf = data.mrr_waterfall

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ fontFamily: 'var(--serif)', fontSize: '28px', fontWeight: 600, color: 'var(--ink)', margin: 0 }}>
          Facturacao
        </h1>
        <div style={{ display: 'flex', gap: '4px' }}>
          {[30, 60, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              style={{
                padding: '6px 14px', borderRadius: '8px', border: '1px solid var(--border)',
                background: days === d ? 'var(--ink)' : 'transparent',
                color: days === d ? '#fff' : 'var(--ink-light)',
                fontFamily: 'var(--sans)', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
              }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* MRR Waterfall */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <KpiCard label="New MRR" value={`$${wf.new_mrr.toFixed(0)}`} accent />
        <KpiCard label="Expansion" value={`$${wf.expansion_mrr.toFixed(0)}`} />
        <KpiCard label="Churned" value={`-$${wf.churned_mrr.toFixed(0)}`} warning={wf.churned_mrr > 0} />
        <KpiCard
          label="Net MRR"
          value={`${wf.net_mrr >= 0 ? '+' : ''}$${wf.net_mrr.toFixed(0)}`}
          accent={wf.net_mrr > 0}
          warning={wf.net_mrr < 0}
        />
      </div>

      {/* Failed payments */}
      {data.failed_payments.length > 0 && (
        <div
          style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '20px',
          }}
        >
          <div style={{ fontFamily: 'var(--serif)', fontSize: '18px', fontWeight: 600, color: '#dc2626', marginBottom: '12px' }}>
            Pagamentos Falhados ({data.failed_payments.length})
          </div>
          {data.failed_payments.map((evt) => (
            <div
              key={evt.id}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0',
                borderBottom: '1px solid #fecaca',
                fontFamily: 'var(--sans)', fontSize: '13px',
              }}
            >
              <span style={{ color: '#991b1b', fontWeight: 500 }}>{evt.customer_id || '—'}</span>
              <span style={{ color: '#dc2626' }}>${evt.amount_usd?.toFixed(2) || '0'}</span>
              <span style={{ color: '#991b1b', fontSize: '12px', marginLeft: 'auto' }}>
                {new Date(evt.occurred_at).toLocaleDateString('pt-PT')}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Event timeline */}
      <div
        style={{
          background: 'rgba(255,255,255,0.8)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px',
        }}
      >
        <div style={{ fontFamily: 'var(--serif)', fontSize: '18px', fontWeight: 600, color: 'var(--ink)', marginBottom: '16px' }}>
          Eventos ({data.total_events})
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {data.events.slice(0, 50).map((evt) => {
            const isChurn = evt.event_type.includes('deleted')
            const isFailed = evt.event_type.includes('failed')
            const severity = isFailed ? 'critical' : isChurn ? 'warning' : 'info'

            return (
              <div
                key={evt.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '10px 14px', borderRadius: '8px',
                  border: '1px solid var(--border)',
                  fontFamily: 'var(--sans)', fontSize: '13px',
                }}
              >
                <AlertBadge severity={severity} />
                <span style={{ flex: 1, color: 'var(--ink)' }}>
                  {EVENT_LABELS[evt.event_type] || evt.event_type}
                </span>
                <span style={{ color: 'var(--ink-light)' }}>
                  {evt.customer_id?.slice(-8) || '—'}
                </span>
                {evt.amount_usd != null && evt.amount_usd > 0 && (
                  <span style={{ fontWeight: 600, color: 'var(--ink)' }}>
                    ${evt.amount_usd.toFixed(2)}
                  </span>
                )}
                <span style={{ color: 'var(--ink-muted)', fontSize: '12px' }}>
                  {new Date(evt.occurred_at).toLocaleDateString('pt-PT')}
                </span>
              </div>
            )
          })}
          {data.events.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ink-muted)' }}>
              Sem eventos no periodo
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
